import { ListViewType } from "@/constants";
import { FlightItem } from "@/typings";

const sortFunction = (a: FlightItem, b: FlightItem) => {
  if (a.award_points > b.award_points) {
    return 1;
  } else if (a.fare.travel_minutes_total > b.fare.travel_minutes_total) {
    return 1;
  } else if (a.surcharge > b.surcharge) {
    return 1;
  } else if (a.fare.products[0].departure_time > b.fare.products[0].departure_time) {
    return 1;
  } else if (
    a.fare.products[a.fare.products.length - 1].arrival_time >
    b.fare.products[b.fare.products.length - 1].arrival_time
  ) {
    return 1;
  }

  return -1;
};

const sortByEconomyMilesWithEmptyLast = (
  a: FlightItem,
  b: FlightItem,
  path: keyof FlightItem["cabin_prices"],
  isAsc: boolean
) => {
  const aHasValue = a.cabin_prices?.[path]?.miles !== undefined;
  const bHasValue = b.cabin_prices?.[path]?.miles !== undefined;

  // 如果 a 没有值而 b 有值，将 a 排在后面
  if (!aHasValue && bHasValue) {
    return 1;
  }
  // 如果 b 没有值而 a 有值，将 b 排在后面
  if (!bHasValue && aHasValue) {
    return -1;
  }
  // 如果两者都没有值，保持原来的顺序
  if (!aHasValue && !bHasValue) {
    return 0;
  }
  // 两者都有值，按照 miles 的大小排序
  const result = a.cabin_prices[path].miles - b.cabin_prices[path].miles;
  return isAsc ? result : -result;
};

export const SORT_OPTIONS = [
  {
    value: 1,
    label: `Lowest Points`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.award_points < b.award_points) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 6,
    label: `Lowest Economy Class Points`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Economy", true);
    },
    condition: (viewType: number) => {
      return viewType === ListViewType.Grid;
    },
    type: 1,
  },
  {
    value: 7,
    label: `Lowest Economy Premium Class Points`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Premium Economy", true);
    },
    condition: (viewType: number) => {
      return viewType === ListViewType.Grid;
    },
    type: 1,
  },
  {
    value: 8,
    label: `Lowest Business Class Points`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Business", true);
    },
    condition: (viewType: number) => {
      return viewType === ListViewType.Grid;
    },
    type: 1,
  },
  {
    value: 9,
    label: `Lowest First Class Points`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "First", true);
    },
    condition: (viewType: number) => {
      return viewType === ListViewType.Grid;
    },
    type: 1,
  },
  {
    value: 2,
    label: `Quickest Flights`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.fare.travel_minutes_total < b.fare.travel_minutes_total) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 10,
    label: `Longest Flights`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.fare.travel_minutes_total > b.fare.travel_minutes_total) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 3,
    label: `Lowest Tax`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.surcharge < b.surcharge) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 4,
    label: `Departure Time`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.fare.products[0].departure_time < b.fare.products[0].departure_time) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 5,
    label: `Arrival time`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (
        a.fare.products[a.fare.products.length - 1].arrival_time <
        b.fare.products[b.fare.products.length - 1].arrival_time
      ) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 6,
    label: `Economy Class`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Economy", true);
    },
    type: 2,
  },
  {
    value: 62,
    label: `Economy Class`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Economy", false);
    },
    type: 2,
  },
  {
    value: 7,
    label: `Premium Economy`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Premium Economy", true);
    },
    type: 2,
  },
  {
    value: 64,
    label: `Premium Economy`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Premium Economy", false);
    },
    type: 2,
  },
  {
    value: 8,
    label: `Business Class`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Business", true);
    },
    type: 2,
  },
  {
    value: 66,
    label: `Business Class`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "Business", false);
    },
    type: 2,
  },
  {
    value: 9,
    label: `First`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "First", true);
    },
    type: 2,
  },
  {
    value: 68,
    label: `First`,
    fn: (a: FlightItem, b: FlightItem) => {
      return sortByEconomyMilesWithEmptyLast(a, b, "First", false);
    },
    type: 2,
  },
  {
    value: 90,
    label: `Least Connection`,
    fn: (a: FlightItem, b: FlightItem) => {
      const a_products = a?.fare?.products || [];
      const b_products = b?.fare?.products || [];
      // Sum the layover time of each flight
      const a_layover_total = a_products.reduce((acc, cur) => acc + cur.layover_time, 0);
      const b_layover_total = b_products.reduce((acc, cur) => acc + cur.layover_time, 0);
      if (a_layover_total < b_layover_total) {
        return -1;
      }
      return sortFunction(a, b);
    },
    type: 1,
  },
  {
    value: 91,
    label: `Last Seen`,
    fn: (a: FlightItem, b: FlightItem) => {
      if (a.ls > b.ls) {
        return -1;
      }

      return 1;
    },
    type: 1,
  },
];

/**
 * 排序
 */
export const sortFn = (list: FlightItem[], sortKey: number): FlightItem[] => {
  const targetSort = SORT_OPTIONS.find((item) => item.value === sortKey);
  return targetSort?.fn ? list.sort(targetSort?.fn) : list;
};
